"use client"

import { useState, useMemo, useEffect } from "react"
import { NavLink, TextInput, Stack, Text, Box, Group, Badge } from "@forestsync/ui"
import { TbSearch, TbTag, TbClock, TbCertificate, TbTrees } from "react-icons/tb"
import { useTranslations } from "next-intl"
import { useRouter, useSearchParams } from "next/navigation"
import { ACCREDITATION_STANDARD_LABELS } from "@forestsync/db"
import type { MarketSummary } from "@/lib/queries"

interface MarketTreeProps {
  markets: MarketSummary[]
  selectedCreditType?: string
  selectedVintage?: number
  selectedProjectRefId?: string
  onSelect?: (creditType: string, vintage: number, projectRefId: string) => void
}

// Groups projects by their declared lifespan commitment.
// Lifespan is selected from fixed presets (10/20/50/100/200/1000) so we label directly.
function permanenceBucket(lifespan: number | null): string {
  if (!lifespan || lifespan <= 0) return "N/A"
  return `${lifespan}+`
}

function standardLabel(std: string | null): string {
  if (!std) return "N/A"
  return ACCREDITATION_STANDARD_LABELS[std] ?? std
}

function VintageLabel({
  vintage,
  lastPrice,
  change24hPct,
  bestBid,
  bestAsk,
}: {
  vintage: number
  lastPrice: number | null
  change24hPct: number | null
  bestBid: number | null
  bestAsk: number | null
}) {
  const hasPrice = lastPrice != null
  const changePositive = (change24hPct ?? 0) >= 0

  return (
    <Group justify="space-between" wrap="nowrap" gap={4} style={{ width: "100%" }}>
      <Stack gap={0}>
        <Text size="sm">{vintage}</Text>
        {(bestBid != null || bestAsk != null) && (
          <Group gap={2} wrap="nowrap">
            <Text size="xs" c="green">{bestBid != null ? bestBid.toFixed(2) : "N/A"}</Text>
            <Text size="xs" c="dimmed">·</Text>
            <Text size="xs" c="red">{bestAsk != null ? bestAsk.toFixed(2) : "N/A"}</Text>
          </Group>
        )}
      </Stack>
      {hasPrice && (
        <Stack gap={2} align="flex-end" style={{ flexShrink: 0 }}>
          <Text size="xs" fw={600} c={changePositive ? "green" : "red"} style={{ whiteSpace: "nowrap" }}>
            €{lastPrice!.toFixed(2)}
          </Text>
          {change24hPct != null && (
            <Badge size="xs" variant="light" color={changePositive ? "green" : "red"} style={{ whiteSpace: "nowrap" }}>
              {changePositive ? "+" : ""}{change24hPct.toFixed(2)}%
            </Badge>
          )}
        </Stack>
      )}
    </Group>
  )
}

// tree structure: technology → permanence → standard → { projectRefId → { projectName, vintages[] } }
type TreeNode = Record<string, Record<string, Record<string, Record<string, { projectName: string; vintages: MarketSummary[] }>>>>

export function MarketTree({ markets, selectedCreditType, selectedVintage, selectedProjectRefId, onSelect }: MarketTreeProps) {
  const t = useTranslations("trading")
  const router = useRouter()
  const searchParams = useSearchParams()
  const [search, setSearch] = useState("")

  const activeCreditType = selectedCreditType ?? searchParams.get("creditType") ?? undefined
  const activeVintage = selectedVintage ?? (searchParams.get("vintage") ? Number(searchParams.get("vintage")) : undefined)
  const activeProjectRefId = selectedProjectRefId ?? searchParams.get("projectRefId") ?? undefined

  const [openedTech, setOpenedTech] = useState<Set<string>>(() => new Set(activeCreditType ? [activeCreditType] : []))
  const [openedPerm, setOpenedPerm] = useState<Set<string>>(() => new Set())
  const [openedStd, setOpenedStd] = useState<Set<string>>(() => new Set())
  const [openedProj, setOpenedProj] = useState<Set<string>>(() => new Set(
    activeCreditType && activeProjectRefId ? [`${activeCreditType}::${activeProjectRefId}`] : []
  ))

  // Auto-open active path on selection change
  useEffect(() => {
    if (!activeCreditType) return
    setOpenedTech((prev) => new Set([...prev, activeCreditType]))
    if (activeProjectRefId) {
      const active = markets.find((m) => m.creditType === activeCreditType && m.projectRefId === activeProjectRefId)
      if (active) {
        const perm = permanenceBucket(active.lifespan)
        const std = active.accreditationStandard ?? null
        setOpenedPerm((prev) => new Set([...prev, `${activeCreditType}::${perm}`]))
        setOpenedStd((prev) => new Set([...prev, `${activeCreditType}::${perm}::${std}`]))
        setOpenedProj((prev) => new Set([...prev, `${activeCreditType}::${activeProjectRefId}`]))
      }
    }
  }, [activeCreditType, activeProjectRefId, markets])

  const tree = useMemo((): TreeNode => {
    const lower = search.toLowerCase()
    const filtered = lower
      ? markets.filter(
          (m) =>
            m.creditType.toLowerCase().includes(lower) ||
            String(m.vintage).includes(lower) ||
            m.projectName.toLowerCase().includes(lower) ||
            (m.methodology ?? "").toLowerCase().includes(lower) ||
            (m.accreditationStandard ?? "").toLowerCase().includes(lower)
        )
      : markets

    const node: TreeNode = {}
    for (const m of filtered) {
      const tech = m.creditType
      const perm = permanenceBucket(m.lifespan)
      const std = m.accreditationStandard ?? null
      const stdKey = String(std)

      if (!node[tech]) node[tech] = {}
      if (!node[tech][perm]) node[tech][perm] = {}
      if (!node[tech][perm][stdKey]) node[tech][perm][stdKey] = {}
      if (!node[tech][perm][stdKey][m.projectRefId]) {
        node[tech][perm][stdKey][m.projectRefId] = { projectName: m.projectName, vintages: [] }
      }
      node[tech][perm][stdKey][m.projectRefId].vintages.push(m)
    }
    return node
  }, [markets, search])

  function navigateToMarket(m: MarketSummary) {
    if (onSelect) {
      onSelect(m.creditType, m.vintage, m.projectRefId)
    } else {
      router.push(`/market?creditType=${encodeURIComponent(m.creditType)}&vintage=${m.vintage}&projectRefId=${encodeURIComponent(m.projectRefId)}`)
    }
  }

  function toggle<T extends string>(set: Set<T>, key: T, opened: boolean): Set<T> {
    const s = new Set(set)
    opened ? s.add(key) : s.delete(key)
    return s
  }

  return (
    <Stack gap={0} h="100%" style={{ overflowY: "auto" }}>
      <Box p={8} pt={20} style={{ flexShrink: 0 }}>
        <TextInput
          placeholder={t("marketTree.search")}
          leftSection={<TbSearch size={14} />}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          size="xs"
        />
      </Box>

      {Object.keys(tree).length === 0 ? (
        <Text size="xs" c="dimmed" ta="center" p={12}>
          {t("marketTree.noResults")}
        </Text>
      ) : (
        Object.entries(tree).map(([tech, permGroups]) => (
          <NavLink
            key={tech}
            label={<Text size="sm" fw={700} tt="uppercase">{tech}</Text>}
            leftSection={<TbTag size={16} />}
            opened={openedTech.has(tech)}
            onChange={(o) => setOpenedTech((prev) => toggle(prev, tech, o))}
            childrenOffset={0}
          >
            {Object.entries(permGroups).map(([perm, stdGroups]) => {
              const permKey = `${tech}::${perm}`
              return (
                <NavLink
                  key={permKey}
                  label={<Text size="xs" fw={600} c="dimmed" tt="uppercase" style={{ letterSpacing: "0.04em" }}>{perm}</Text>}
                  leftSection={<TbClock size={13} />}
                  childrenOffset={0}
                  opened={openedPerm.has(permKey)}
                  onChange={(o) => setOpenedPerm((prev) => toggle(prev, permKey, o))}
                  pl={12}
                >
                  {Object.entries(stdGroups).map(([stdKey, projects]) => {
                    const nodeKey = `${tech}::${perm}::${stdKey}`
                    const label = standardLabel(stdKey === "null" ? null : stdKey)
                    return (
                      <NavLink
                        key={nodeKey}
                        label={<Text size="xs" fw={500} c="dimmed">{label}</Text>}
                        leftSection={<TbCertificate size={13} />}
                        childrenOffset={0}
                        opened={openedStd.has(nodeKey)}
                        onChange={(o) => setOpenedStd((prev) => toggle(prev, nodeKey, o))}
                        pl={24}
                      >
                        {Object.entries(projects).map(([projectRefId, { projectName, vintages }]) => {
                          const projKey = `${tech}::${projectRefId}`
                          return (
                            <NavLink
                              key={projKey}
                              label={<Text size="xs" fw={500}>{projectName}</Text>}
                              leftSection={<TbTrees size={14} />}
                              childrenOffset={0}
                              opened={openedProj.has(projKey)}
                              onChange={(o) => setOpenedProj((prev) => toggle(prev, projKey, o))}
                              pl={36}
                            >
                              {vintages.map((m) => (
                                <NavLink
                                  key={`${m.creditType}::${m.projectRefId}::${m.vintage}`}
                                  label={
                                    <VintageLabel
                                      vintage={m.vintage}
                                      lastPrice={m.lastPrice}
                                      change24hPct={m.change24hPct}
                                      bestBid={m.bestBid}
                                      bestAsk={m.bestAsk}
                                    />
                                  }
                                  active={
                                    m.creditType === activeCreditType &&
                                    m.vintage === activeVintage &&
                                    m.projectRefId === activeProjectRefId
                                  }
                                  onClick={() => navigateToMarket(m)}
                                  pl={52}
                                />
                              ))}
                            </NavLink>
                          )
                        })}
                      </NavLink>
                    )
                  })}
                </NavLink>
              )
            })}
          </NavLink>
        ))
      )}
    </Stack>
  )
}
